import { LitElement, html, css, styles, createRef, ref } from '@hh/global.js'

class HHBackInStock extends LitElement {
  static properties = {
    loaded: {
      type: Boolean,
      reflect: true,
    },
    open: {
      type: Boolean,
      reflect: true,
    },
    'api-key': {
      type: String,
      reflect: true,
    },
    'initial-variant-id': {
      type: String,
      reflect: true,
    },
    _variant: {
      type: Object,
      state: true,
    },
    _success: {
      state: true,
    },
    _error: {
      state: true,
    },
  }

  static styles = [
    styles.hidden,
    css`
      :host .container {
        padding: 2rem;
        border: 0.1rem solid var(--hh-color-brand-secondary);
        position: relative;
      }

      :host .close {
        width: 1rem;
        height: 1rem;
        position: absolute;
        top: 2rem;
        right: 2rem;
      }

      :host .close ::slotted(button) {
        color: currentColor;
        display: block;
        width: 100%;
        height: 100%;
        padding: 0;
        margin: 0;
        border: none;
        border-radius: 0;
        background: none;
        cursor: pointer;
      }

      :host .container,
      :host([open]) .main-button {
        display: none;
      }

      :host([open]) .container {
        display: block;
      }

      :host ::slotted(p) {
        margin: 0;
      }

      :host ::slotted([slot='terms-input']) {
        position: relative;
        z-index: 1;
      }

      :host .message {
        padding-right: 2rem;
      }

      :host .message-error p {
        margin-top: 1rem;
        margin-bottom: 0;
        color: #c50000;
      }
    `,
  ]

  $form = createRef()
  $emailSlot = createRef()
  $termsSlot = createRef()

  constructor() {
    super()

    this._success = false
    this._error = false

    this.onVariantChange = this.onVariantChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.loaded = true
    window.addEventListener('variant:change', this.onVariantChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('variant:change', this.onVariantChange)
  }

  onVariantChange(event) {
    this._variant = event.detail.variant
    this.classList.toggle('hidden', this._variant.available === true)
    this.open = false
  }

  toggleOpen() {
    this.open = !this.open
  }

  async submit(event) {
    event.preventDefault()

    if (!this.validate()) {
      return
    }

    const email = this.$emailSlot?.value
      ?.assignedElements()[0]
      ?.querySelector('input')
    const variantId = this._variant?.id || this['initial-variant-id']

    const options = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        revision: '2024-07-15',
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email.value
                }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      })
    };

    await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this['api-key']}`, options)
      .then(response => {
        if (response.status == 202) {
          this._success = true
        } else {
          return response.json()
        }
      })
      .then(error => {
        this._error = error.errors[0].detail
       })
      .catch(err => {
        console.error(err)
      });
  }

  // Implement custom form validation,
  // due to shadow DOM form limitations
  validate() {
    const terms = this.$termsSlot?.value
      ?.assignedElements()[0]
      ?.querySelector('input')
    const email = this.$emailSlot?.value
      ?.assignedElements()[0]
      ?.querySelector('input')
    const termsChecked = terms?.checked
    const emailValid = email?.validity?.valid
    if (!termsChecked) terms.reportValidity()
    if (!emailValid) email.reportValidity()
    return termsChecked && emailValid
  }

  render() {
    return html`
      <div class="main-button">
        <slot name="button" @click="${this.toggleOpen}"></slot>
      </div>

      <div class="container">
        <div class="close">
          <slot name="close" @click="${this.toggleOpen}"></slot>
        </div>

        <div class="inner ${this._success ? 'hidden' : ''}">
          <slot name="title"></slot>
          <div class="form">
            <form
              method="post"
              @submit="${this.submit}"
              ${ref(this.$form)}
            >
              <input type="hidden" name="a" value="${this['api-key']}" />
              <input
                type="hidden"
                name="variant"
                value="${this._variant?.id || this['initial-variant-id']}"
              />
              <input type="hidden" name="platform" value="shopify" />
              <slot name="email-input" ${ref(this.$emailSlot)}></slot>
              <slot name="terms-input" ${ref(this.$termsSlot)}></slot>
              <slot name="submit-input" @click="${this.submit}"></slot>
            </form>
          </div>
        </div>

        <div class="message${this._success || this._error ? '' : ' hidden'}">
          <div class="message-success${this._success ? '' : ' hidden'}">
            <slot name="message-success"></slot>
          </div>

          <div class="message-error${this._error && !this._success ? '' : ' hidden'}">
            <p>${this._error ? this._error : ''}</p>
          </div>
        </div>
      </div>
    `
  }
}

customElements.define('hh-back-in-stock', HHBackInStock)
